import {
	EmailDeliveryStatus,
	EmailRecipientDeliveryStatus,
	EmailRecipientKind,
	EmailRecipientSendStatus,
	EmailSenderChannel,
	EmailSendSource
} from "~/gql/graphqlBase.generated";

export interface EmailStatusChip {
	color: "default" | "error" | "info" | "success" | "warning";
	label: string;
}

type CompatibleEmailSendSource = EmailSendSource | "BENCHMARK" | "WHATS_NEW";

const SOURCE_LABELS: Record<CompatibleEmailSendSource, string> = {
	BENCHMARK: "Benchmark",
	WHATS_NEW: "What's new",
	[EmailSendSource.AutoInvoice]: "Automatic Invoice",
	[EmailSendSource.Load]: "Freight Quote",
	[EmailSendSource.LoadStop]: "ETA Notice",
	[EmailSendSource.ManualReport]: "Manual Report",
	[EmailSendSource.Notification]: "Notification",
	[EmailSendSource.OrderAcknowledgment]: "Order Acknowledgment",
	[EmailSendSource.OrderConfirmation]: "Order Confirmation",
	[EmailSendSource.PersonalAccount]: "Personal Email",
	[EmailSendSource.ScheduledReport]: "Scheduled Report"
};

export const emailSendSourceLabel = (source: CompatibleEmailSendSource): string =>
	SOURCE_LABELS[source] ?? String(source);

const DELIVERY_STATUS_CHIPS: Record<EmailDeliveryStatus, EmailStatusChip> = {
	[EmailDeliveryStatus.Blocked]: { color: "error", label: "Blocked" },
	[EmailDeliveryStatus.Bounced]: { color: "error", label: "Bounced" },
	[EmailDeliveryStatus.Deferred]: { color: "warning", label: "Deferred" },
	[EmailDeliveryStatus.Delivered]: { color: "success", label: "Delivered" },
	[EmailDeliveryStatus.Dropped]: { color: "error", label: "Dropped" },
	[EmailDeliveryStatus.Failed]: { color: "error", label: "Failed" },
	[EmailDeliveryStatus.PartiallyDelivered]: { color: "warning", label: "Partially Delivered" },
	[EmailDeliveryStatus.Pending]: { color: "default", label: "Pending" },
	[EmailDeliveryStatus.SpamReport]: { color: "warning", label: "Spam Report" }
};

export const emailDeliveryStatusChip = (status: EmailDeliveryStatus): EmailStatusChip =>
	DELIVERY_STATUS_CHIPS[status] ?? DELIVERY_STATUS_CHIPS[EmailDeliveryStatus.Pending];

const NOT_SUPPORTED_DELIVERY_CHIP: EmailStatusChip = { color: "default", label: "Not Supported" };

const WEBHOOK_TRACKED_DELIVERY_CHANNELS = new Set<EmailSenderChannel>([
	EmailSenderChannel.SendgridDomain,
	EmailSenderChannel.SendgridSingle
]);

const channelHasWebhookDeliveryTracking = (channel: EmailSenderChannel | null | undefined): boolean =>
	!channel || WEBHOOK_TRACKED_DELIVERY_CHANNELS.has(channel);

export const deriveDeliveryStatusDisplay = (
	channel: EmailSenderChannel | null | undefined,
	deliveryStatus: EmailDeliveryStatus
): EmailStatusChip =>
	channelHasWebhookDeliveryTracking(channel) ? emailDeliveryStatusChip(deliveryStatus) : NOT_SUPPORTED_DELIVERY_CHIP;

const RECIPIENT_DELIVERY_CHIPS: Record<EmailRecipientDeliveryStatus, EmailStatusChip> = {
	[EmailRecipientDeliveryStatus.Blocked]: { color: "error", label: "Blocked" },
	[EmailRecipientDeliveryStatus.Bounced]: { color: "error", label: "Bounced" },
	[EmailRecipientDeliveryStatus.Deferred]: { color: "warning", label: "Deferred" },
	[EmailRecipientDeliveryStatus.Delivered]: { color: "success", label: "Delivered" },
	[EmailRecipientDeliveryStatus.Dropped]: { color: "error", label: "Dropped" },
	[EmailRecipientDeliveryStatus.Opened]: { color: "info", label: "Opened" },
	[EmailRecipientDeliveryStatus.Pending]: { color: "default", label: "Pending" },
	[EmailRecipientDeliveryStatus.SpamReport]: { color: "warning", label: "Spam Report" }
};

export const recipientDeliveryStatusChip = (status: EmailRecipientDeliveryStatus): EmailStatusChip =>
	RECIPIENT_DELIVERY_CHIPS[status] ?? RECIPIENT_DELIVERY_CHIPS[EmailRecipientDeliveryStatus.Pending];

const RECIPIENT_SEND_CHIPS: Record<EmailRecipientSendStatus, EmailStatusChip> = {
	[EmailRecipientSendStatus.Failed]: { color: "error", label: "Send Failed" },
	[EmailRecipientSendStatus.Sent]: { color: "success", label: "Sent" }
};

export const recipientSendStatusChip = (status: EmailRecipientSendStatus): EmailStatusChip =>
	RECIPIENT_SEND_CHIPS[status] ?? RECIPIENT_SEND_CHIPS[EmailRecipientSendStatus.Sent];

export const splitRecipientsByKind = <T extends { kind: EmailRecipientKind }>(recipients: readonly T[]) => ({
	cc: recipients.filter((recipient) => recipient.kind === EmailRecipientKind.Cc),
	to: recipients.filter((recipient) => recipient.kind === EmailRecipientKind.To)
});

export const anyRecipientSendFailed = (recipients: readonly { sendStatus: EmailRecipientSendStatus }[]): boolean =>
	recipients.some((recipient) => recipient.sendStatus === EmailRecipientSendStatus.Failed);
